import { t } from 'i18next';
import { equals, isEmpty, omit, path } from 'ramda';
import React from 'react';
import { Trans } from 'react-i18next';

import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { InputLabel, CheckBox, Text } from '../common';

import {
  WHWrapS,
  WHDayRowS,
  WHRowContentS,
  WHRowCheckboxWrapS,
  WHTextS,
  WHDayS,
  WHByAppointmentWrapS,
  WHSelectWrS,
  WHSelectS,
  WHBtnS,
  WHAddIcon,
  BlueCloseIcon,
  SummarySelectionWrapper,
} from './companyEditS';

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAYS_SHORTCUTS = WEEK_DAYS.reduce((acc, dayName) => {
  acc[dayName] = t(`companyEdit.workingHours.days.short.${dayName}`);
  return acc;
}, {});

const BY_APPOINTMENT_VALUE = 'by_appointment';
const DEFAULT_TIME_RANGE = {
  FROM: '08:00',
  TO: '17:00',
};

const HOURS_IN_DAY = new Array(24).fill(null).flatMap((_, index) => {
  const hour = index < 10 ? `0${index}` : index;
  return [`${hour}:00`, `${hour}:30`];
});

export const getDayInShortForm = (key) =>
  key.includes('-')
    ? key
        .split('-')
        .map((day) => DAYS_SHORTCUTS[day].toUpperCase())
        .join('-')
    : DAYS_SHORTCUTS[key].toUpperCase();

export const getComposedWorkingHours = (selectedHours) =>
  WEEK_DAYS.reduce((acc, day) => {
    const dayHours = path([day], selectedHours);
    if (!isEmpty(dayHours)) {
      const previousDayName = path([WEEK_DAYS.indexOf(day) - 1], WEEK_DAYS);
      const isByAppointment = dayHours.includes(BY_APPOINTMENT_VALUE);
      const isEqualToPreviousDay =
        !isByAppointment && day !== 'monday' && equals(dayHours, selectedHours[previousDayName]);

      if (isEqualToPreviousDay) {
        const keyToUpdate = Object.keys(acc)[Object.keys(acc).length - 1];
        const newKey = keyToUpdate.includes('-') ? `${keyToUpdate.split('-')[0]}-${day}` : `${keyToUpdate}-${day}`;
        return omit([keyToUpdate], { ...acc, [newKey]: acc[keyToUpdate] });
      } else {
        return {
          ...acc,
          [day]: isByAppointment
            ? t('companyEdit.workingHours.byAppointment')
            : dayHours.map((t) => t.join('-')).join(', '),
        };
      }
    }
    return acc;
  }, {});

const WorkingHours = ({ initialState, onWorkingHoursChange }) => {
  const { width: windowWidth } = useWindowSize();

  const handleTimeChange = (day, timeFrame, index, type) => (event) => {
    const frame = event.target.value;
    const newTimeFrame =
      type === 'from'
        ? [frame, timeFrame ? timeFrame[1] : DEFAULT_TIME_RANGE.TO]
        : [timeFrame ? timeFrame[0] : DEFAULT_TIME_RANGE.FROM, frame];

    const updatedDayValue = initialState[day].map((el, i) => (i === index ? newTimeFrame : el));
    const newWorkingHours = { ...initialState, [day]: updatedDayValue };

    onWorkingHoursChange(newWorkingHours);
  };

  const handleAddTimeFrame = (day) => () => {
    const newDayValue = [...initialState[day], Object.values(DEFAULT_TIME_RANGE)];
    const newWorkingHours = { ...initialState, [day]: newDayValue };
    onWorkingHoursChange(newWorkingHours);
  };

  const handleRemoveTimeFrame = (day, index) => () => {
    const newDayValue = initialState[day].filter((_, i) => i !== index);
    const newWorkingHours = { ...initialState, [day]: newDayValue };
    onWorkingHoursChange(newWorkingHours);
  };

  const handleDayCheckChange = (day) => () => {
    const isDaySelected = !isEmpty(initialState[day]);
    const newWorkingHours = {
      ...initialState,
      [day]: isDaySelected ? [] : [Object.values(DEFAULT_TIME_RANGE)],
    };
    onWorkingHoursChange(newWorkingHours);
  };

  const handleByAppointmentChange = (day) => () => {
    const isByAppointmentSelected = initialState[day].includes(BY_APPOINTMENT_VALUE);
    const newDayValue = isByAppointmentSelected
      ? initialState[day].filter((frame) => frame !== BY_APPOINTMENT_VALUE)
      : [...initialState[day], BY_APPOINTMENT_VALUE];
    const newWorkingHours = { ...initialState, [day]: newDayValue };
    onWorkingHoursChange(newWorkingHours);
  };

  const renderTimeSelection = (day, timeFrame, isRowDisabled, index) => {
    const fromTime = timeFrame ? timeFrame[0] : DEFAULT_TIME_RANGE.FROM;
    const toTime = timeFrame ? timeFrame[1] : DEFAULT_TIME_RANGE.TO;

    return (
      <>
        <WHSelectWrS aItems="center">
          <WHTextS disabled={isRowDisabled}>
            <Trans i18nKey="companyEdit.workingHours.form" />
          </WHTextS>
          <WHSelectS
            width="90px"
            disabled={isRowDisabled}
            value={fromTime}
            onChange={handleTimeChange(day, timeFrame, index, 'from')}
          >
            {HOURS_IN_DAY.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </WHSelectS>
        </WHSelectWrS>

        <WHSelectWrS aItems="center">
          <WHTextS disabled={isRowDisabled}>
            <Trans i18nKey="companyEdit.workingHours.to" />
          </WHTextS>
          <WHSelectS
            width="90px"
            disabled={isRowDisabled}
            value={toTime}
            onChange={handleTimeChange(day, timeFrame, index, 'to')}
          >
            {HOURS_IN_DAY.slice(HOURS_IN_DAY.indexOf(fromTime) + 1).map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </WHSelectS>
        </WHSelectWrS>
      </>
    );
  };

  const renderPartialTimeFrames = (day, isRowDisabled) =>
    initialState[day]
      .filter((frame) => frame !== BY_APPOINTMENT_VALUE)
      .slice(1)
      .map((timeFrame, index) => (
        <WHRowContentS key={index}>
          {renderTimeSelection(day, timeFrame, isRowDisabled, index + 1)}
          <WHBtnS type="button" disabled={isRowDisabled} onClick={handleRemoveTimeFrame(day, index + 1)}>
            <BlueCloseIcon />
            <Text>
              <Trans i18nKey="companyEdit.workingHours.close" />
            </Text>
          </WHBtnS>
        </WHRowContentS>
      ));

  const renderSummary = () => {
    const composedHours = getComposedWorkingHours(initialState);
    return (
      !isEmpty(composedHours) && (
        <SummarySelectionWrapper>
          <InputLabel className="summary-label">{t('companyEdit.labels.workingHoursSelection')}</InputLabel>
          {Object.keys(composedHours).map((dayKey) => (
            <div key={dayKey} className="summary-time-item">
              <b>{getDayInShortForm(dayKey)}</b>: {composedHours[dayKey]}
            </div>
          ))}
        </SummarySelectionWrapper>
      )
    );
  };

  return (
    <DisplayFlexS direction="column" directionScreenMedium="column">
      <InputLabel>{t('companyEdit.labels.workingHours')}</InputLabel>
      <WHWrapS direction="column" directionScreenMedium="column">
        {WEEK_DAYS.map((dayName) => {
          const dayHours = initialState[dayName] || [];
          const isDaySelected = !isEmpty(dayHours);
          const isByAppointmentSelected = dayHours.includes(BY_APPOINTMENT_VALUE);
          const isRowDisabled = !isDaySelected || isByAppointmentSelected;

          return (
            <WHDayRowS key={dayName} direction="column" directionScreenMedium="column">
              <DisplayFlexS key={dayName} directionScreenMedium="column">
                <WHRowCheckboxWrapS>
                  <CheckBox checked={isDaySelected} onChange={handleDayCheckChange(dayName)} />
                  <WHDayS fontFamily="bold">{t(`companyEdit.workingHours.days.full.${dayName}`)}</WHDayS>
                </WHRowCheckboxWrapS>

                <DisplayFlexS direction="column" directionScreenMedium="column">
                  {windowWidth > SCREEN_SIZES.MEDIUM ? (
                    <WHRowContentS directionScreenMedium="column">
                      {renderTimeSelection(dayName, dayHours[0], isRowDisabled, 0)}

                      <WHBtnS type="button" disabled={isRowDisabled} onClick={handleAddTimeFrame(dayName)}>
                        <WHAddIcon />
                        <Text>
                          <Trans i18nKey="companyEdit.workingHours.add" />
                        </Text>
                      </WHBtnS>
                    </WHRowContentS>
                  ) : Array.isArray(dayHours[0]) ? (
                    <WHRowContentS directionScreenMedium="column">
                      {renderTimeSelection(dayName, dayHours[0], isRowDisabled, 0)}

                      <WHBtnS type="button" disabled={isRowDisabled} onClick={handleAddTimeFrame(dayName)}>
                        <WHAddIcon />
                        <Text>
                          <Trans i18nKey="companyEdit.workingHours.add" />
                        </Text>
                      </WHBtnS>
                    </WHRowContentS>
                  ) : null}

                  {dayHours.length > 1 && renderPartialTimeFrames(dayName, isRowDisabled)}
                </DisplayFlexS>
              </DisplayFlexS>

              {isDaySelected && (
                <WHByAppointmentWrapS>
                  <label>
                    <CheckBox
                      checked={isByAppointmentSelected}
                      onChange={handleByAppointmentChange(dayName)}
                      disabled={!isByAppointmentSelected && isRowDisabled}
                    />
                    <Text>
                      <Trans i18nKey="companyEdit.workingHours.byAppointment" />
                    </Text>
                  </label>
                </WHByAppointmentWrapS>
              )}
            </WHDayRowS>
          );
        })}
      </WHWrapS>
      {renderSummary()}
    </DisplayFlexS>
  );
};

export default WorkingHours;
